/**
 * Shared visual language for line / area charts.
 *
 * Ported from the ouidevis dashboard acceptance chart (recharts) so both products
 * read the same: monotone curve, soft vertical gradient fill, hollow white dots,
 * horizontal-only grid, borderless axes and a light card tooltip.
 *
 * Series colours stay per-chart — the gradient is derived from whatever colour a
 * dataset already uses, so semantic palettes (green / red sentiment) keep meaning.
 */

export const CHART_FONT_FAMILY = "DM Sans";
export const CHART_GRID_COLOR = "#E5E7EB";
export const CHART_TICK_COLOR = "#9CA3AF";
export const CHART_LABEL_COLOR = "#111827";
export const CHART_PRIMARY_COLOR = "#4318FF";

// Past this many points the dots turn into noise, so we hide them and rely on the
// hover dot instead. The reference chart plots ~12 months and always shows them.
const MAX_POINTS_WITH_DOTS = 24;

const parseColor = (color) => {
  if (typeof color !== "string") return null;
  const value = color.trim();

  const rgbMatch = value.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  if (rgbMatch) {
    return { r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) };
  }

  let hex = value.replace("#", "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  if (hex.length !== 6 || Number.isNaN(Number.parseInt(hex, 16))) return null;

  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
};

export const withAlpha = (color, alpha) => {
  const rgb = parseColor(color);
  if (!rgb) return color;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

// Mixes a colour toward white; the reference gradient starts on a tint of the
// stroke colour rather than the stroke colour itself.
export const lighten = (color, amount) => {
  const rgb = parseColor(color);
  if (!rgb) return color;
  const mix = (channel) => Math.round(channel + (255 - channel) * amount);
  return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
};

export const createAreaGradient = (ctx, chartArea, color) => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, withAlpha(lighten(color, 0.25), 0.75));
  gradient.addColorStop(1, withAlpha(color, 0));
  return gradient;
};

// Scriptable fill: the chart area only exists after the first layout pass, so fall
// back to a flat tint on the very first paint.
export const areaFill = (color) => (context) => {
  const { ctx, chartArea } = context.chart || {};
  if (!ctx || !chartArea) return withAlpha(color, 0.15);
  return createAreaGradient(ctx, chartArea, color);
};

const pointRadiusFor = (radius) => (context) => {
  const count = context.chart?.data?.labels?.length || 0;
  return count > MAX_POINTS_WITH_DOTS ? 0 : radius;
};

/**
 * Visual props for one line dataset. Merge over an existing dataset — `data`,
 * `label` and any `borderDash` stay untouched.
 */
export const lineDatasetStyle = (color = CHART_PRIMARY_COLOR, { fill = true } = {}) => ({
  borderColor: color,
  borderWidth: 2,
  // recharts' type="monotone"; chart.js ignores `tension` in this mode.
  cubicInterpolationMode: "monotone",
  tension: 0,
  fill,
  backgroundColor: fill ? areaFill(color) : withAlpha(color, 0),
  pointRadius: pointRadiusFor(4),
  pointBackgroundColor: "#ffffff",
  pointBorderColor: color,
  pointBorderWidth: 2,
  pointHoverRadius: 5,
  pointHoverBackgroundColor: color,
  pointHoverBorderColor: color,
  pointHoverBorderWidth: 2,
  pointHitRadius: 12,
});

export const chartTooltipStyle = {
  backgroundColor: "#ffffff",
  titleColor: CHART_LABEL_COLOR,
  bodyColor: "#4B5563",
  borderColor: CHART_GRID_COLOR,
  borderWidth: 1,
  cornerRadius: 8,
  padding: 10,
  usePointStyle: true,
  boxWidth: 8,
  boxHeight: 8,
  boxPadding: 4,
  titleFont: { family: CHART_FONT_FAMILY, size: 12, weight: "600" },
  bodyFont: { family: CHART_FONT_FAMILY, size: 12 },
};

const tickStyle = {
  font: { family: CHART_FONT_FAMILY, size: 12 },
  color: CHART_TICK_COLOR,
  padding: 8,
};

/**
 * Axis defaults: horizontal grid lines only, no axis line, no tick marks.
 * Pass `x` / `y` to merge chart-specific bits (min, max, tick callbacks…).
 */
export const lineChartScales = ({ x = {}, y = {} } = {}) => ({
  y: {
    ...y,
    grid: { color: CHART_GRID_COLOR, drawTicks: false, ...(y.grid || {}) },
    border: { display: false, ...(y.border || {}) },
    ticks: { ...tickStyle, ...(y.ticks || {}) },
  },
  x: {
    ...x,
    grid: { display: false, drawTicks: false, ...(x.grid || {}) },
    border: { display: false, ...(x.border || {}) },
    ticks: { ...tickStyle, autoSkip: true, ...(x.ticks || {}) },
  },
});

export const chartLegendStyle = {
  position: "top",
  align: "end",
  labels: {
    boxWidth: 8,
    boxHeight: 8,
    usePointStyle: true,
    pointStyle: "circle",
    font: { family: CHART_FONT_FAMILY, size: 11 },
    color: "#6B7280",
  },
};

/**
 * Base options for a line chart. `plugins` and `scales` are merged so callers keep
 * their own tooltip callbacks / axis bounds.
 */
export const lineChartBaseOptions = ({ plugins = {}, scales = {}, ...rest } = {}) => {
  const { tooltip = {}, legend = {}, ...otherPlugins } = plugins;

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    ...rest,
    plugins: {
      datalabels: { display: false },
      ...otherPlugins,
      legend: legend.display === false ? { display: false } : { ...chartLegendStyle, ...legend },
      tooltip: { ...chartTooltipStyle, ...tooltip },
    },
    scales: lineChartScales(scales),
  };
};
